import { EntityIconType } from '@common/Entity/constants';
import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, getSqlIDEUrl, Private_Routes } from '@common/url';
import { titleCase } from '@common/utils/metadataUtils';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useImportEntityChanges } from '@pages/Transpiler/useImportEntityChanges';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { AlertTriangleIcon, ArrowNarrowUpRightIcon, InfoCircleIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import {
  Badges,
  Bar,
  ColumnsType,
  Dialog,
  ExcelTable,
  GhostInput,
  overlayStyle,
  Select,
  Text,
  Spinner,
  Stack,
  StackItem,
  TableActions,
  TableIconButton,
  Tooltip
} from '@prophecy/ui-v3';
import { StyledIconContainer } from '@prophecy/ui-v3/Dialog/styled';
import { StyledInputContainer } from '@prophecy/ui-v3/Input/styled';
import { FilterType } from '@prophecy/ui-v3/Table/types';
import { isCaseInsensitiveEqual } from '@prophecy/utils/string';
import { validateEntityName } from '@prophecy/utils/validation';
import { useQueryClient } from '@tanstack/react-query';
import { importData } from 'frontend/core/src/data/apis/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useImmer } from 'use-immer';

import { ChangeEntityImportType, OperationTypes, OperationTypesLabel } from '../../../../../data/apis/transpileTypes';
import { errorHandler, useRestMutation } from '../../../../../data/util';
import { QueryKey } from '../../../config';
import { StyledNameContainer } from '../../../ImportedInfo/util';
import { useImportState } from '../../useImportState';
import { ImportNameFilterOption } from '../types';

const StyledSelect = styled(Select)``;

const StyledGhostInput = styled(GhostInput)`
  &&&&& input {
    min-width: 200px;
  }
`;
const StyledBar = styled(Bar)`
  border-width: 0;
  display: contents;
  width: 460px;
  color: ${theme.colors.warning700};
  background: ${theme.colors.warning25};
`;
const DuplicateClass = 'duplicate';
const StyledDialog = styled(Dialog)`
  width: 70%;
  height: 600px;
  && tr.${DuplicateClass}, && tr.${DuplicateClass}:hover {
    td,
    ${StyledSelect} div,
    ${StyledSelect} svg,
    ${StyledSelect} span,
    ${StyledGhostInput}, ${StyledInputContainer} {
      background: ${theme.colors.warning50}!important;
    }
  }
`;
type ItemType = ChangeEntityImportType & { rowKey: string };

export const AcceptChanges = ({
  hasToggle,
  toggle,
  disabled
}: {
  hasToggle?: boolean;
  toggle: () => void;
  disabled?: boolean;
}) => {
  const {
    transpilerState: { transpilerId },
    formDataWithActions: {
      formContext: { getValues }
    },
    transpileGraphState: {
      clientState: {
        transpileSummary: { isSuccess }
      }
    },
    actions: { handleStepChange }
  } = useImportState();

  const projectId = getValues('projectId');
  const isSql = getValues('language') === BackEndCodeLanguage.sql;

  const { data, refetch, isLoading, isError, error } = useImportEntityChanges(transpilerId, disabled);

  const [selectedRows, setSelectedRows] = useState<Array<string>>([]);

  const [validNames, setValidNames] = useImmer<{ [key: string]: boolean }>({});
  const clientQuery = useQueryClient();

  const importErrorMessage = 'An error occurred while importing';
  const importMutation = useRestMutation<{ success: boolean }, ChangeEntityImportType[]>(
    (data) => importData(transpilerId, data),
    {
      retry: false,
      onSuccess: (_resp) => {
        handleStepChange(5);
        toggle();
        clientQuery.invalidateQueries([QueryKey]);
      },
      onError: (error) => {
        try {
          const errorMessage = error.message ? `${importErrorMessage}: ${error.message}` : importErrorMessage;
          errorHandler(errorMessage);
        } catch {
          errorHandler(importErrorMessage);
        }
        refetch();
      },
      sentryTag: 'transpiler.importPipelineJobAndDatasets'
    }
  );

  const onSubmit = async (data: ChangeEntityImportType[]) => {
    const currentData = data
      .filter(({ rowKey = '' }) => selectedRows.includes(rowKey))
      .map(({ __path, isDuplicate: _isDuplicate, rowKey: _rowKey, ...rest }) => rest);
    await importMutation.mutateAsync(currentData);
  };
  const prepareData = useCallback((data: ChangeEntityImportType[]): ItemType[] => {
    return data.map((row, i) => ({ ...row, rowKey: i + 'changeEntityname_' + row.originalName }));
  }, []);
  const importDataList = useMemo(() => prepareData(data?.data || []), [data?.data, prepareData]);
  const [changesEntityData, setChangesData] = useImmer(importDataList);

  useEffect(() => {
    if (importDataList.length > 0) {
      setChangesData(importDataList);
      const selectedKeys: Set<string> = new Set();
      importDataList.forEach((row) => {
        selectedKeys.add(row.rowKey);
      });
      setSelectedRows([...selectedKeys]);
    }
  }, [importDataList, setChangesData, prepareData]);

  const updateEntityData = (row: ItemType) => {
    const isValid = validateEntityName(row.updatedName ? row.updatedName : row.originalName);
    setValidNames((draft) => {
      draft[row.rowKey] = !isValid;
    });
    setChangesData((draft) => {
      return draft.map((item) => (item.rowKey === row.rowKey ? { ...item, ...row } : item));
    });
  };

  const datasetColumns: ColumnsType<ItemType> = [
    {
      key: 'updatedName',
      dataIndex: 'updatedName',
      title: 'Entity',
      width: '250px',
      filter: {
        type: FilterType.list,
        defaultValue: [ImportNameFilterOption.all],
        condition: (row, value) => {
          const isUnique = value.includes(ImportNameFilterOption.unique);
          const isDuplicate = value.includes(ImportNameFilterOption.duplicate);
          if (value.includes(ImportNameFilterOption.all) || (isDuplicate && isUnique)) {
            return true;
          } else if (isDuplicate) {
            return Boolean(row.isDuplicate);
          } else {
            return isUnique ? Boolean(!row.isDuplicate) : false;
          }
        },
        options: Object.values(ImportNameFilterOption).map((value) => ({
          label: titleCase(value),
          value
        }))
      },
      render: (name = '', row) => {
        const { entityType, operation } = row;
        const isEditDisabled =
          isCaseInsensitiveEqual(entityType as string, Entity.Pipeline) ||
          isCaseInsensitiveEqual(entityType as string, Entity.StoredProcedure) ||
          isCaseInsensitiveEqual(entityType as string, Entity.Model) ||
          isCaseInsensitiveEqual(entityType as string, Entity.Job) ||
          (operation === OperationTypes.create_new && row.duplicate === false) ||
          (isSql && isCaseInsensitiveEqual(entityType, Entity.UDF));

        return isEditDisabled ? (
          renderColumnWithEllipsis(name)
        ) : (
          <Tooltip
            overlayStyle={overlayStyle}
            title={!name ? 'Name is required' : row.isDuplicate ? `Rename duplicate name ${name}` : `Name: ${name}`}>
            <Stack height='100%' width='100%' align='center'>
              <StyledGhostInput
                inputSize='m'
                variant={!name || validNames[row.rowKey] === false ? 'error' : 'primary'}
                value={name}
                onChange={(value) => updateEntityData({ ...row, updatedName: value })}
                placeholder={row.originalName}
                data-test-id='transpiler-accept-changes-rename-input'
              />
            </Stack>
          </Tooltip>
        );
      }
    },
    {
      key: 'entityType',
      dataIndex: 'entityType',
      title: 'Type',
      sortable: true,
      render: (type: string) => {
        const isUDF = isCaseInsensitiveEqual(type, Entity.UDF);
        const isDataset = isCaseInsensitiveEqual(type, Entity.Dataset) || isCaseInsensitiveEqual(type, Entity.Table);
        const isSP = isCaseInsensitiveEqual(type, Entity.StoredProcedure);
        let finalType = type;
        if (isDataset && isSql) {
          finalType = Entity.Table;
        } else if (isUDF) {
          finalType = Entity.Function;
        } else if (isSP) {
          finalType = Entity.StoredProcedure;
        }
        return (
          <Stack direction='horizontal' gap={theme.spaces.x6} width='100%' alignY='center'>
            <EntityIcon entity={(isSP ? finalType : titleCase(finalType)) as EntityIconType} size='xs' />
            <StyledNameContainer grow='1'>{renderColumnWithEllipsis(titleCase(finalType))}</StyledNameContainer>
          </Stack>
        );
      }
    },
    {
      key: 'parentEntityName',
      dataIndex: 'parentEntityName',
      title: 'ParentEntity',
      render: (parentEntity: string, rowData) => {
        return (
          <Stack direction='horizontal' gap={theme.spaces.x6} width='100%' alignY='center'>
            <EntityIcon entity={titleCase(rowData.parentEntityType as string) as EntityIconType} size='xs' />
            <StyledNameContainer grow='1'>{renderColumnWithEllipsis(parentEntity)}</StyledNameContainer>
          </Stack>
        );
      }
    },
    {
      key: 'operation',
      dataIndex: 'operation',
      title: 'Operation',
      width: '120px',
      render: (operation, row) => {
        const opts = OperationTypesLabel[operation as OperationTypes];
        return (
          <Tooltip title={row.isDuplicate ? `Rename duplicate` : ''}>
            <Stack alignY='center' width='100%'>
              <Badges size='m' tone={opts.tone as BaseColor}>
                {opts.label}
              </Badges>
            </Stack>
          </Tooltip>
        );
      }
    },
    {
      align: 'center',
      dataIndex: 'actions',
      key: 'actions',
      width: '50px',
      resizable: false,
      render: (_: string, rowData) => {
        const entityId = rowData.entityId;
        const isDatasetTable =
          isCaseInsensitiveEqual(rowData.entityType as string, Entity.Dataset) ||
          isCaseInsensitiveEqual(rowData.entityType as string, Entity.Table);
        const isJob = isCaseInsensitiveEqual(rowData.entityType as string, Entity.Job);
        let url = getIDEUrl({
          uid: entityId as string,
          entity: isJob ? Entity.Job : Entity.Pipeline
        });

        const name = rowData.originalName;

        if (isDatasetTable) {
          url = isSql
            ? getSqlIDEUrl({ uid: projectId as string, table: name })
            : Private_Routes.Dataset.home.getUrl({ uid: entityId as string });
        } else if (isSql) {
          url = getSqlIDEUrl({ uid: projectId, entity: Entity.Model.toLowerCase(), name });
        }

        const isSqlFunctionOverride =
          rowData.operation === OperationTypes.overwrite &&
          isCaseInsensitiveEqual(Entity.UDF, rowData.entityType) &&
          isSql;

        if (isSqlFunctionOverride) {
          url = getSqlIDEUrl({ uid: projectId as string, entity: Entity.Macro.toLowerCase(), name });
        }

        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} alignY='center'>
            <TableActions gap={theme.spaces.x12} alignY='center'>
              {(entityId || isSqlFunctionOverride) && (
                <TableIconButton
                  icon={<ArrowNarrowUpRightIcon type='default' />}
                  elementType='link'
                  to={url}
                  aria-label={`View ${rowData.originalName}`}
                  data-test-id='accept-changes-view-details-button'
                />
              )}
            </TableActions>
          </Stack>
        );
      }
    }
  ];

  let hasDuplicate = false;
  const nameList: string[] = [];
  const tableData = changesEntityData.map((row) => {
    const originalName = row.originalName;
    const isSelected = selectedRows.includes(row.rowKey);
    let operation = row.operation ? row.operation : isSelected ? OperationTypes.create_new : OperationTypes.skip;
    const name = row.updatedName ?? row.originalName;
    const isOverwriteOrDuplicate = row.duplicate || operation === OperationTypes.overwrite;
    const duplicate = Boolean(nameList.includes(name) && isOverwriteOrDuplicate);
    nameList.push(name);
    if (!hasDuplicate) {
      hasDuplicate = duplicate;
    }

    if (
      !hasDuplicate &&
      isOverwriteOrDuplicate &&
      originalName !== name &&
      (isCaseInsensitiveEqual(row.entityType as string, Entity.Dataset) ||
        isCaseInsensitiveEqual(row.entityType as string, Entity.UDF))
    ) {
      operation = OperationTypes.create_new;
    }
    return { ...row, isDuplicate: duplicate, operation, updatedName: name };
  });
  const isSelectedRowDuplicate = tableData.some((row) => row.isDuplicate && selectedRows.includes(row.rowKey));
  return (
    <StyledDialog
      data-test-id='transpiler-accept-changes-dialog'
      onOk={() => onSubmit(tableData)}
      okButton={{
        children: 'Import',
        variant: 'primary',
        loading: importMutation.isLoading,
        disabled:
          isLoading ||
          !isSuccess ||
          !!isSelectedRowDuplicate ||
          Object.values(validNames).filter((v) => v === false).length > 0
      }}
      size='l'
      icon={
        <StyledIconContainer iconPlacement='left' tone={hasDuplicate ? BaseColor.warning : BaseColor.primary}>
          {hasDuplicate ? (
            <AlertTriangleIcon type='default' data-state='warning' />
          ) : (
            <InfoCircleIcon type='default' data-state='info' />
          )}
        </StyledIconContainer>
      }
      iconPlacement='right'
      open={hasToggle}
      closeButton={null}
      onClose={toggle}
      subTitle={`The following entities are going to be overwritten by the newly transpiled ones. You can simply disable the entities you don't want to transpile.`}
      title={`Choose entities to create`}>
      {isError ? (
        <Text level='sm' tone={theme.colors.error700}>
          {error?.message ? `An error occurred: ${error.message}` : 'Some error occurred.'}
        </Text>
      ) : (
        <Stack gap={theme.spaces.x24} width='100%' height='400px' padding={`${theme.spaces.x24} ${theme.spaces.x4}`}>
          {hasDuplicate && (
            <StyledBar orientation='horizontal' tone='warning'>
              Some files are duplicate. Please review and rename one of them.
            </StyledBar>
          )}
          <StackItem grow='1'>
            {isLoading ? (
              <Spinner spinning={true} />
            ) : (
              <ExcelTable
                rowSelection={{
                  selectedRowKeys: selectedRows,
                  onChange: (selectedArray) => setSelectedRows(selectedArray as string[])
                }}
                rowKey={'rowKey'}
                onRow={(row) => {
                  return {
                    className: row.isDuplicate && selectedRows.includes(row.rowKey as string) ? DuplicateClass : ''
                  } as React.TdHTMLAttributes<HTMLTableRowElement>;
                }}
                fixedHeader={true}
                columns={datasetColumns}
                width='100%'
                height='100%'
                placeholderRows={tableData.length < 9 ? 7 : 0}
                virtualize={true}
                dataSource={tableData}
                data-test-id='transpiler-accept-changes-table'
              />
            )}
          </StackItem>
        </Stack>
      )}
    </StyledDialog>
  );
};
